// RecurringTasksView — define recurring rules that auto-create real Tasks
// every N days/weeks/months/years. The backend cron runs every 5 minutes.

import React, { useEffect, useMemo, useState, useCallback } from "react";
import DateField from './DateField.jsx';
import { TASK_TYPES, useAgencyTaskTypes } from './TasksView.jsx';
import {
  Plus, Edit2, Trash2, Pause, Play, Zap, Clock, X, AlertCircle,
  Calendar, Loader2, RefreshCw, Repeat,
} from "lucide-react";
import {
  listRecurringTasks, createRecurringTask, updateRecurringTask,
  deleteRecurringTask, pauseRecurringTask, resumeRecurringTask, runRecurringTaskNow,
} from "../api.js";
import { toast } from "./Toast.jsx";
import { confirmDialog } from "./ui.jsx";
import { attendOptions, seatLabelFor } from "../atiende.js";

const FREQUENCIES = [
  { value: "DAILY",   labelEs: "Diaria",  labelEn: "Daily" },
  { value: "WEEKLY",  labelEs: "Semanal", labelEn: "Weekly" },
  { value: "MONTHLY", labelEs: "Mensual", labelEn: "Monthly" },
  { value: "YEARLY",  labelEs: "Anual",   labelEn: "Yearly" },
];

const PRIORITIES = [
  { value: "LOW",    labelEs: "Baja",    labelEn: "Low" },
  { value: "MEDIUM", labelEs: "Media",   labelEn: "Medium" },
  { value: "HIGH",   labelEs: "Alta",    labelEn: "High" },
  { value: "URGENT", labelEs: "Urgente", labelEn: "Urgent" },
];

const DAYS_OF_WEEK_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const DAYS_OF_WEEK_EN = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function RecurringTasksView({ currentUser, clients = [], users = [], seatLabels = {}, lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | rule object
  const [busy, setBusy] = useState(null); // ruleId currently being acted on

  const refresh = useCallback(async () => {
    setLoading(true); setError("");
    try { setRules(await listRecurringTasks()); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const h = (e) => { if (e?.detail?.view === "recurring") setEditing(null); };
    window.addEventListener("deskte:nav-reset", h);
    return () => window.removeEventListener("deskte:nav-reset", h);
  }, []);

  async function doRunNow(rule) {
    setBusy(rule.id); setError("");
    try {
      await runRecurringTaskNow(rule.id);
      await refresh();
      toast.success(t("Tarea creada para hoy. Revisa tu lista de Tareas.",
              "Task created for today. Check your Tasks list."));
    } catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }
  async function doPause(rule) {
    setBusy(rule.id);
    try { await pauseRecurringTask(rule.id); await refresh(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }
  async function doResume(rule) {
    setBusy(rule.id);
    try { await resumeRecurringTask(rule.id); await refresh(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }
  async function doDelete(rule) {
    if (!(await confirmDialog({ message: t(`¿Eliminar la regla "${rule.title}"? Las tareas ya creadas se mantienen.`,
                          `Delete rule "${rule.title}"? Already-created tasks are kept.`), danger: true }))) return;
    setBusy(rule.id);
    try { await deleteRecurringTask(rule.id); await refresh(); }
    catch (err) { setError(err.message); }
    finally { setBusy(null); }
  }

  if (editing) {
    return (
      <RecurringEditor
        initial={editing === "new" ? null : editing}
        clients={clients}
        users={users}
        seatLabels={seatLabels}
        currentUser={currentUser}
        lang={lang}
        onCancel={() => setEditing(null)}
        onSaved={async () => { await refresh(); setEditing(null); }}
      />
    );
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 1100, margin: "0 auto" }}>
      <Header lang={lang} onCreate={() => setEditing("new")} />

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
          padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
          display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span style={{ flex: 1 }}>{error}</span>
          <button onClick={() => setError("")} style={{ background: "none", border: "none", color: "#fca5a5", cursor: "pointer" }}>×</button>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={20} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : rules.length === 0 ? (
        <EmptyState lang={lang} onCreate={() => setEditing("new")} />
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(380px,1fr))", gap: "1rem" }}>
          {rules.map((r) => (
            <RuleCard
              key={r.id}
              rule={r}
              lang={lang}
              busy={busy === r.id}
              onEdit={() => setEditing(r)}
              onDelete={() => doDelete(r)}
              onPause={() => doPause(r)}
              onResume={() => doResume(r)}
              onRunNow={() => doRunNow(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".75rem" }}>
      <div>
        <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
          🔁 {t("Tareas recurrentes", "Recurring tasks")}
        </h2>
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", marginTop: 2 }}>
          {t("Define una vez, se crea automáticamente cada día/semana/mes. Las tareas reales aparecen en tu lista de Tareas.",
             "Define once, auto-creates daily/weekly/monthly. The real tasks show up in your Tasks list.")}
        </div>
      </div>
      <button onClick={onCreate} style={primaryBtn()}>
        <Plus size={16} /> {t("Nueva tarea recurrente", "New recurring task")}
      </button>
    </div>
  );
}

function EmptyState({ lang, onCreate }) {
  const t = (es, en) => (lang === "es" ? es : en);
  return (
    <div style={{
      textAlign: "center", padding: "3rem 1rem",
      background: "var(--c-surface,#0d1c2e)", border: "1px dashed var(--c-border,#1a3550)",
      borderRadius: 16,
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: 16, margin: "0 auto 1rem",
        background: "#a855f722", border: "1px solid #a855f766",
        display: "grid", placeItems: "center",
      }}>
        <Repeat size={28} color="#a855f7" />
      </div>
      <h3 style={{ margin: "0 0 .5rem", color: "var(--c-white,#eef6ff)", fontSize: "1.1rem" }}>
        {t("Aún no tienes tareas recurrentes", "No recurring tasks yet")}
      </h3>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: ".88rem", margin: "0 0 1.5rem", maxWidth: 460, marginInline: "auto", lineHeight: 1.55 }}>
        {t("Ejemplos: 'Llamar a Carlos cada lunes', 'Revisar pólizas Medicare cada 1 de mes', 'Reporte semanal cada viernes'.",
           "Examples: 'Call Carlos every Monday', 'Review Medicare policies on the 1st of each month', 'Weekly report every Friday'.")}
      </p>
      <button onClick={onCreate} style={primaryBtn()}>
        <Plus size={16} /> {t("Crear la primera", "Create the first one")}
      </button>
    </div>
  );
}

function RuleCard({ rule, lang, busy, onEdit, onDelete, onPause, onResume, onRunNow }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const freqLabel = FREQUENCIES.find((f) => f.value === rule.frequency);
  const everyN = rule.intervalCount > 1
    ? t(`cada ${rule.intervalCount}`, `every ${rule.intervalCount}`)
    : "";
  const dowLabel = rule.dayOfWeek != null
    ? (lang === "es" ? DAYS_OF_WEEK_ES[rule.dayOfWeek] : DAYS_OF_WEEK_EN[rule.dayOfWeek])
    : null;

  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 14, padding: "1.1rem",
      display: "flex", flexDirection: "column", gap: ".7rem",
      opacity: rule.isActive ? 1 : 0.65,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--c-white,#eef6ff)", marginBottom: 4 }}>
            {rule.title}
          </div>
          {rule.client && (
            <div style={{ fontSize: ".75rem", color: "var(--c-cyan,#00d4ff)", marginBottom: 4 }}>
              👤 {rule.client.firstName} {rule.client.lastName}
            </div>
          )}
        </div>
        <span style={{
          fontSize: ".68rem", padding: "3px 9px", borderRadius: 999, fontWeight: 700, letterSpacing: ".04em",
          background: rule.isActive ? "#10b98122" : "#94a3b822",
          color:      rule.isActive ? "#10b981"   : "#94a3b8",
        }}>
          {rule.isActive ? "ACTIVE" : "PAUSED"}
        </span>
      </div>

      {rule.description && (
        <div style={{ fontSize: ".82rem", color: "var(--c-text2,#7aa3c0)", lineHeight: 1.5 }}>
          {rule.description}
        </div>
      )}

      {/* Schedule summary */}
      <div style={{
        background: "var(--c-surface2,#112538)", borderRadius: 10, padding: ".65rem .9rem",
        fontSize: ".8rem", color: "var(--c-text,#c8dff0)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
          <Repeat size={13} color="#a855f7" />
          {everyN ? `${everyN} ` : ""}{freqLabel ? (lang === "es" ? freqLabel.labelEs : freqLabel.labelEn).toLowerCase() : rule.frequency}
          {dowLabel ? ` — ${dowLabel}` : ""}
          {rule.dayOfMonth != null ? ` — ${t("día", "day")} ${rule.dayOfMonth}` : ""}
          {/* "First <weekday> of the month" is stored as MONTHLY + dayOfWeek
              with no dayOfMonth; say so instead of showing nothing. */}
          {rule.frequency === "MONTHLY" && rule.dayOfMonth == null && rule.dayOfWeek != null
            ? ` — ${t("primer", "first")} ${dowLabel}`
            : ""}
          {rule.timeOfDay ? ` @ ${rule.timeOfDay}` : ""}
        </div>
        <div style={{ fontSize: ".72rem", color: "var(--c-text3,#45688a)", marginTop: 4 }}>
          {rule.isActive
            ? <><Clock size={10} style={{ verticalAlign: "-1px" }} /> {t("Próxima ejecución", "Next run")}: {fmtDateTime(rule.nextRunAt, lang)}</>
            : t("Pausada", "Paused")}
          {rule.occurrenceCount > 0 && ` · ${rule.occurrenceCount} ${t("ejecutada(s)", "run(s)")}`}
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <button onClick={onRunNow} disabled={busy} style={btn("primary")} title={t("Crear una tarea ahora (testing)", "Create one task now (testing)")}>
          {busy ? <Loader2 size={11} className="dkt-spin" /> : <Zap size={11} />}
          {t("Ejecutar ahora", "Run now")}
        </button>
        {rule.isActive ? (
          <button onClick={onPause} disabled={busy} style={btn("ghost")}>
            <Pause size={11} /> {t("Pausar", "Pause")}
          </button>
        ) : (
          <button onClick={onResume} disabled={busy} style={btn("ghost")}>
            <Play size={11} /> {t("Reactivar", "Resume")}
          </button>
        )}
        <button onClick={onEdit} disabled={busy} style={btn("ghost")}>
          <Edit2 size={11} /> {t("Editar", "Edit")}
        </button>
        <button onClick={onDelete} disabled={busy} style={btn("danger")}>
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Editor (create / edit)
// ----------------------------------------------------------------------

function RecurringEditor({ initial, clients, users, seatLabels, currentUser, onCancel, onSaved, lang }) {
  const t = (es, en) => (lang === "es" ? es : en);
  const [title, setTitle] = useState(initial?.title || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [priority, setPriority] = useState(initial?.priority || "MEDIUM");
  const [category, setCategory] = useState(initial?.category || "");
  // The agency's own task types, from the same store Tareas writes to.
  const [taskTypes] = useAgencyTaskTypes();
  const [clientId, setClientId] = useState(initial?.clientId || "");
  // A quien le llegan las tareas que crea la regla (el ownerId en el servidor).
  // Sin elegir, quien la crea, como siempre.
  const [assigneeId, setAssigneeId] = useState(initial?.ownerId || currentUser?.id || "");
  const [frequency, setFrequency] = useState(initial?.frequency || "WEEKLY");
  const [intervalCount, setIntervalCount] = useState(initial?.intervalCount || 1);
  const [dayOfWeek, setDayOfWeek] = useState(initial?.dayOfWeek ?? 1);
  const [dayOfMonth, setDayOfMonth] = useState(initial?.dayOfMonth ?? 1);
  // A saved MONTHLY rule with no dayOfMonth but a dayOfWeek IS the
  // "first <weekday>" shape — that's how the backend stores it.
  const [monthlyMode, setMonthlyMode] = useState(
    initial?.frequency === "MONTHLY" && initial?.dayOfMonth == null && initial?.dayOfWeek != null ? "weekday" : "day",
  );
  const [timeOfDay, setTimeOfDay] = useState(initial?.timeOfDay || "09:00");
  const [endsAt, setEndsAt] = useState(initial?.endsAt ? initial.endsAt.split("T")[0] : "");
  const [maxOccurrences, setMaxOccurrences] = useState(initial?.maxOccurrences || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    if (!title.trim()) { setError(t("El título es obligatorio.", "Title is required.")); return; }
    setBusy(true); setError("");
    try {
      const payload = {
        title, description: description || null,
        priority, category: category || null,
        clientId: clientId || null,
        assigneeId: assigneeId || null,
        frequency, intervalCount,
        monthlyMode: frequency === "MONTHLY" ? monthlyMode : undefined,
        dayOfWeek: (frequency === "WEEKLY" || (frequency === "MONTHLY" && monthlyMode === "weekday")) ? dayOfWeek : null,
        dayOfMonth: (frequency === "MONTHLY" && monthlyMode === "day") ? dayOfMonth : null,
        timeOfDay,
        // Parse the date-only value at LOCAL midnight. `new Date("YYYY-MM-DD")`
        // parses as UTC midnight, which in Florida (UTC-4/-5) is the previous
        // evening — the end date drifted a day earlier (feedback_local_day_not_utc).
        endsAt: endsAt ? new Date(endsAt + "T00:00:00").toISOString() : null,
        maxOccurrences: maxOccurrences ? parseInt(maxOccurrences, 10) : null,
      };
      if (initial?.id) await updateRecurringTask(initial.id, payload);
      else await createRecurringTask(payload);
      await onSaved();
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ padding: "1.5rem", maxWidth: 800, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.25rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div>
          <button onClick={onCancel} style={{ background: "none", border: "none", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", fontSize: ".82rem", marginBottom: 6 }}>
            ← {t("Volver", "Back")}
          </button>
          <h2 style={{ margin: 0, fontSize: "1.4rem", color: "var(--c-white,#eef6ff)" }}>
            {initial?.id ? t("Editar tarea recurrente", "Edit recurring task") : t("Nueva tarea recurrente", "New recurring task")}
          </h2>
        </div>
        <button onClick={save} disabled={busy} style={primaryBtn()}>
          {busy ? <Loader2 size={14} className="dkt-spin" /> : null}
          {busy ? t("Guardando…", "Saving…") : (initial?.id ? t("Guardar cambios", "Save changes") : t("Crear", "Create"))}
        </button>
      </div>

      {error && (
        <div style={{
          background: "#3a1a1a", border: "1px solid #ef4444", borderRadius: 10,
          padding: ".75rem 1rem", marginBottom: 12, color: "#fca5a5",
          display: "flex", alignItems: "center", gap: 10, fontSize: ".88rem",
        }}>
          <AlertCircle size={16} /><span>{error}</span>
        </div>
      )}

      <Card title={t("Tarea", "Task")}>
        <Field label={t("Título", "Title")} required>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
                 placeholder={t("ej. Llamar a Carlos para chequeo mensual",
                                "e.g. Call Carlos for monthly check-in")}
                 style={inputStyle()} />
        </Field>
        <Field label={t("Descripción", "Description")}>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2} style={{ ...inputStyle(), resize: "vertical" }} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label={t("Prioridad", "Priority")}>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} style={inputStyle()}>
              {PRIORITIES.map((p) => (<option key={p.value} value={p.value}>{lang === "es" ? p.labelEs : p.labelEn}</option>))}
            </select>
          </Field>
          {/* Same type catalogue as a normal task, including the agency's own
              types. This used to be a free-text box with a "follow-up"
              placeholder, so a recurring task could carry a category that no
              filter or coloured pill in Tareas recognised — the two lists had
              silently drifted apart (Lissette 2026-07-21: "en tareas
              recurrentes poder agregar mas tipos de tareas o personalizadas").
              New types are still created in Tareas; here they just show up. */}
          <Field label={t("Tipo de tarea (opcional)", "Task type (optional)")}>
            <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle()}>
              <option value="">{t("— Sin tipo —", "— No type —")}</option>
              {TASK_TYPES.map((tp) => (
                <option key={tp.id} value={tp.id}>{lang === "es" ? tp.es : tp.en}</option>
              ))}
              {(taskTypes || []).filter((ct) => ct && ct.id && ct.label).length > 0 && (
                <optgroup label={t("Tus tipos", "Your types")}>
                  {(taskTypes || []).filter((ct) => ct && ct.id && ct.label).map((ct) => (
                    <option key={ct.id} value={ct.id}>{ct.label}</option>
                  ))}
                </optgroup>
              )}
              {/* A rule saved before the catalogue existed keeps its value
                  instead of silently resetting to "no type" on the next edit. */}
              {category && !TASK_TYPES.some((tp) => tp.id === category)
                && !(taskTypes || []).some((ct) => ct && ct.id === category) && (
                <option value={category}>{category}</option>
              )}
            </select>
          </Field>
        </div>
        <Field label={t("Cliente (opcional)", "Client (optional)")}>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={inputStyle()}>
            <option value="">{t("— Sin cliente (tarea general) —", "— No client (general task) —")}</option>
            {clients.map((cl) => (
              <option key={cl.id} value={cl.id}>{cl.firstName} {cl.lastName}</option>
            ))}
          </select>
        </Field>
        <Field label={t("Asignar a", "Assign to")}>
          <select value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)} style={inputStyle()}>
            {attendOptions(users).map((u) => (
              <option key={u.id} value={u.id}>
                {(seatLabelFor(u.id, seatLabels, users) || {}).name}{u.id === currentUser?.id ? t(" (yo)", " (me)") : ""}
              </option>
            ))}
            {/* Quien la tiene ahora, aunque ya no salga en el equipo: si no, el
                menu enseñaria a otra persona sin que nadie la haya elegido. */}
            {assigneeId && !attendOptions(users).some((u) => u.id === assigneeId) && (
              <option value={assigneeId}>{initial?.owner?.displayName || t("Yo", "Me")}</option>
            )}
          </select>
        </Field>
      </Card>

      <Card title={t("Recurrencia", "Recurrence")}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label={t("Frecuencia", "Frequency")}>
            <select value={frequency} onChange={(e) => setFrequency(e.target.value)} style={inputStyle()}>
              {FREQUENCIES.map((f) => (<option key={f.value} value={f.value}>{lang === "es" ? f.labelEs : f.labelEn}</option>))}
            </select>
          </Field>
          <Field label={t("Cada N", "Every N")}>
            <input type="number" min={1} max={365} value={intervalCount}
                   onChange={(e) => setIntervalCount(parseInt(e.target.value, 10) || 1)} style={inputStyle()} />
            <div style={{ fontSize: ".7rem", color: "var(--c-text3,#45688a)", marginTop: 4 }}>
              {t(`ej. cada ${intervalCount} ${(FREQUENCIES.find((f) => f.value === frequency) || {}).labelEs?.toLowerCase() || ""}`,
                 `e.g. every ${intervalCount} ${(FREQUENCIES.find((f) => f.value === frequency) || {}).labelEn?.toLowerCase() || ""}`)}
            </div>
          </Field>
        </div>

        {frequency === "WEEKLY" && (
          <Field label={t("Día de la semana", "Day of week")}>
            <select value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))} style={inputStyle()}>
              {(lang === "es" ? DAYS_OF_WEEK_ES : DAYS_OF_WEEK_EN).map((d, i) => (
                <option key={i} value={i}>{d}</option>
              ))}
            </select>
          </Field>
        )}

        {frequency === "MONTHLY" && (
          <>
            {/* Two ways to say "monthly": a fixed day number, or the first
                <weekday> of the month (Lissette 2026-07-21: "en meses colocar
                el primer lunes del mes"). */}
            <Field label={t("¿Cuándo del mes?", "When in the month?")}>
              <select value={monthlyMode} onChange={(e) => setMonthlyMode(e.target.value)} style={inputStyle()}>
                <option value="day">{t("Un día fijo (ej. el 15)", "A fixed day (e.g. the 15th)")}</option>
                <option value="weekday">{t("El primer… del mes", "The first… of the month")}</option>
              </select>
            </Field>
            {monthlyMode === "weekday" ? (
              <Field label={t("Primer día de la semana", "First weekday")}>
                <select value={dayOfWeek} onChange={(e) => setDayOfWeek(parseInt(e.target.value, 10))} style={inputStyle()}>
                  {(lang === "es" ? DAYS_OF_WEEK_ES : DAYS_OF_WEEK_EN).map((d, i) => (
                    <option key={i} value={i}>{t(`Primer ${d}`, `First ${d}`)}</option>
                  ))}
                </select>
              </Field>
            ) : (
              <Field label={t("Día del mes (1-28)", "Day of month (1-28)")}>
                <input type="number" min={1} max={28} value={dayOfMonth}
                       onChange={(e) => setDayOfMonth(parseInt(e.target.value, 10) || 1)} style={inputStyle()} />
              </Field>
            )}
          </>
        )}

        <Field label={t("Hora del día", "Time of day")}>
          <input type="time" value={timeOfDay} onChange={(e) => setTimeOfDay(e.target.value)} style={inputStyle()} />
        </Field>
      </Card>

      <Card title={t("Límites (opcional)", "Limits (optional)")}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label={t("Termina el", "Ends on")}>
            <DateField value={endsAt} onChange={(e) => setEndsAt(e.target.value)} style={inputStyle()} />
          </Field>
          <Field label={t("Máximo de ejecuciones", "Max occurrences")}>
            <input type="number" min={1} value={maxOccurrences}
                   onChange={(e) => setMaxOccurrences(e.target.value)}
                   placeholder={t("Sin límite", "No limit")} style={inputStyle()} />
          </Field>
        </div>
      </Card>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------

function Card({ title, children }) {
  return (
    <div style={{
      background: "var(--c-surface,#0d1c2e)", border: "1px solid var(--c-border,#1a3550)",
      borderRadius: 12, padding: "1rem 1.1rem", marginBottom: "1rem",
    }}>
      <div style={{
        fontSize: ".72rem", letterSpacing: ".06em", textTransform: "uppercase",
        color: "var(--c-text3,#45688a)", fontWeight: 700, marginBottom: ".75rem",
      }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: ".75rem" }}>{children}</div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{ fontSize: ".75rem", color: "var(--c-text2,#7aa3c0)", marginBottom: 4, fontWeight: 600 }}>
        {label}{required ? <span style={{ color: "#ef4444", marginLeft: 3 }}>*</span> : null}
      </div>
      {children}
    </label>
  );
}

function inputStyle() {
  return {
    width: "100%", padding: ".55rem .75rem", borderRadius: 8,
    background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)",
    border: "1px solid var(--c-border,#1a3550)", fontSize: ".88rem", outline: "none",
  };
}

function primaryBtn() {
  return {
    padding: ".55rem 1.25rem", borderRadius: 9,
    background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff",
    border: "none", cursor: "pointer", fontWeight: 700, fontSize: ".82rem",
    display: "inline-flex", alignItems: "center", gap: 6,
  };
}

function btn(kind) {
  const map = {
    primary: { bg: "#a855f722", color: "#a855f7", border: "#a855f766" },
    ghost:   { bg: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", border: "var(--c-border,#1a3550)" },
    danger:  { bg: "#7f1d1d22", color: "#fca5a5", border: "#ef444466" },
  }[kind];
  return {
    background: map.bg, color: map.color, border: `1px solid ${map.border}`,
    padding: ".4rem .65rem", borderRadius: 8, cursor: "pointer", fontSize: ".75rem",
    display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600,
  };
}

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch { return "—"; }
}
